import { useEffect, useState } from "react";

import { cn } from "shadcn/lib/utils";

import {
  gauRenameDismissStorageKey,
  isAccountNewerThanGauRename,
} from "./gauRenameNotice";
import { GauTerm } from "./GauTerm";

type GauRenameCalloutProps = {
  className?: string;
  /**
   * The account's creation timestamp (org.created). Accounts created on/after
   * the rename go-live never saw "AI Credits", so the notice is hidden for
   * them. When omitted the notice shows (fail open).
   */
  accountCreatedAt?: Date | string | null;
  /**
   * Current user id, used to scope the per-user dismissal. When omitted the
   * dismissal falls back to a single shared "anonymous" key.
   */
  dismissScopeId?: string | null;
};

/**
 * Rename notice shown on Usage, Spending, and Manage Plan (billing). Explains
 * AI Credits → GAUs; usage and pricing are unchanged. It is dismissible (the
 * dismissal persists in localStorage, scoped per user) and is suppressed for
 * accounts created after the rename went live — they never knew the old term.
 * Meant to be removed once the rename has landed with users.
 */
export function GauRenameCallout({
  className,
  accountCreatedAt,
  dismissScopeId,
}: GauRenameCalloutProps) {
  const storageKey = gauRenameDismissStorageKey(dismissScopeId);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  // `dismissScopeId` (the user id) can hydrate from undefined to a real id
  // after mount, which changes `storageKey`. Re-check the current key so a
  // prior per-user dismissal still hides the notice. One-directional (only
  // hides) so it never clobbers an in-session dismiss.
  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) === "1") setDismissed(true);
    } catch {
      // Best-effort; ignore storage read failures (private mode, etc.).
    }
  }, [storageKey]);

  if (isAccountNewerThanGauRename(accountCreatedAt)) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // Dismissal is best-effort; ignore storage failures (private mode, etc.).
    }
  };

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded border border-border bg-muted/40 px-3 py-2 text-xs leading-4 text-muted-foreground",
        className,
      )}
      data-test="gau-rename-callout"
      role="status"
    >
      <p className="!m-0 flex-1">
        <span className="font-medium text-foreground">
          AI Credits are now called <GauTerm plural />.
        </span>{" "}
        Your usage and pricing are unchanged.
      </p>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded bg-transparent text-muted-foreground/50 transition hover:text-muted-foreground"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 3l8 8M11 3l-8 8" />
        </svg>
      </button>
    </div>
  );
}
